import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import { Button, Dialog, DialogActions, FormControl, FormControlLabel, Grid, MenuItem, Switch } from '@material-ui/core';
import { useCommonStyles } from 'src/styles';
import { CheckIcon, CustomDialogTitle, DateRangeSelect, FieldLabel, MultiSelectControl, SelectControl } from 'src/components';
import { DateRange } from '@material-ui/pickers';
import { Check, Close } from '@material-ui/icons';
import { useStyles } from './styles';

export interface Props {
  open: boolean;
  translation: any;
  currencies: string[];
  dateRange: DateRange<Date>;
  onClose: () => void;
  applyFilter: (currencies: string[], groupBy: string, showInactive: boolean, showOnlyDepositsWithdrawals: boolean, dateRange?: DateRange<Date>) => void;
}

export const FiltersDialog: FC<Props> = ({ open, translation, currencies, dateRange, onClose, applyFilter }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const [selected, setSelected] = useState<string[]>(['All']);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>(dateRange);
  const [inactive, setInactive] = useState<boolean>(false);
  const [onlyDepositsWithdrawals, setOnlyDepositsWithdrawals] = useState<boolean>(false);
  const [groupedBy, setGroupedBy] = useState<string>('noGrouping');

  const applyNewFilter = () => {
    applyFilter(selected, groupedBy, inactive, onlyDepositsWithdrawals, dateRange);
  };

  const clearFilter = () => {
    applyFilter(['All'], '', false, false, undefined);
  };

  const handleClose = () => {
    onClose();
  };

  const selectGroupBy = useCallback((event: ChangeEvent<{ value: any }>) => {
    const newValue = event.target.value;
    setGroupedBy(newValue);
  }, []);

  const handleSelectChange = useCallback(async (event) => {
    let options = event.target.value as string[];
    if (options.length === 0) {
      options = ['All'];
    }

    if (options.length > 1) {
      if (options.includes('All')) {
        if (options[options.length - 1] === 'All') {
          options = options.filter((option) => option === 'All');
        } else {
          options = options.filter((option) => option !== 'All');
        }
      }
    }

    setSelected(options);
  }, []);

  const handleInactiveSwitchChange = useCallback(
    async (event) => {
      event.stopPropagation();
      setInactive(!inactive);
    },
    [inactive],
  );

  const handleOnlyDepositsWithdrawalsSwitchChange = useCallback(
    async (event) => {
      event.stopPropagation();
      setOnlyDepositsWithdrawals(!onlyDepositsWithdrawals);
    },
    [onlyDepositsWithdrawals],
  );

  const renderValueCustomCondition = useCallback(
    (value: string[]) => {
      if (value[0] === 'All') {
        return [translation.all];
      }

      return false;
    },
    [translation.all],
  );

  return (
    <Dialog onClose={handleClose} open={open} classes={{ paper: classes.dialog }}>
      <CustomDialogTitle title={translation.filters} onClose={() => onClose()} />
      <Grid container direction="column">
        <Grid item>
          {currencies.length > 0 && (
            <Grid container direction="column">
              <FieldLabel label={translation.text_6433} />
              <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selected} onChange={(event: any) => handleSelectChange(event)}>
                <MenuItem key="All" className={selected.includes('All') ? classes.selectedOption : classes.option} value="All">
                  <CheckIcon />
                  <span>{translation.all}</span>
                </MenuItem>
                {currencies.map((currency) => (
                  <MenuItem key={currency} className={selected.includes(currency) ? classes.selectedOption : classes.option} value={currency}>
                    <CheckIcon />
                    <span>{currency}</span>
                  </MenuItem>
                ))}
              </MultiSelectControl>
            </Grid>
          )}
        </Grid>
        <Grid item>
          <FieldLabel label={translation.text_5718} />
          <FormControl variant="outlined" className={commonClasses.formControl}>
            <SelectControl value={groupedBy} onChange={selectGroupBy} minWidth={97}>
              <MenuItem className={commonClasses.optionSelector} value="noGrouping">
                {translation.no_grouping}
              </MenuItem>
              <MenuItem className={commonClasses.optionSelector} value="partnershipCode">
                {translation.text_6432}
              </MenuItem>
              <MenuItem className={commonClasses.optionSelector} value="currency">
                {translation.text_6433}
              </MenuItem>
              <MenuItem className={commonClasses.optionSelector} value="clientCountry">
                {translation.text_6434}
              </MenuItem>
            </SelectControl>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControlLabel className={classes.formControlLabel} control={<Switch checked={inactive} classes={{ root: commonClasses.switchRoot }} onChange={handleInactiveSwitchChange} />} label={translation.text_6431} />
        </Grid>
        <Grid item>
          <FormControlLabel
            className={classes.formControlLabel}
            control={<Switch checked={onlyDepositsWithdrawals} classes={{ root: commonClasses.switchRoot }} onChange={handleOnlyDepositsWithdrawalsSwitchChange} />}
            label={translation.show_only_clients_with_deposit_withdrawals}
          />
        </Grid>
        <Grid item>
          {onlyDepositsWithdrawals && (
            <>
              <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
              <DateRangeSelect
                defaultValue={selectedDateRange}
                onRangeChange={(value) => {
                  setSelectedDateRange(value);
                }}
              />
            </>
          )}
        </Grid>
      </Grid>
      <DialogActions className={commonClasses.dialogAction}>
        <Button onClick={applyNewFilter} fullWidth startIcon={<Check />} className={commonClasses.activeDialogBtn}>
          {translation.text_65}
        </Button>
        <Button onClick={clearFilter} fullWidth startIcon={<Close />} className={commonClasses.cancelDialogBtn}>
          {translation.clearAll}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
